import { Alert, Icon, Typography } from "@/components/atoms";
import { flightTerminalLabel } from "@/helpers/bookingHelpers";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { isUnvalidatedLocation } from "@/redux/slices/clientBooking/bookingPayload";
import { DepartureTerminalSource, PickupTerminalSource } from "@/redux/slices/clientBooking/types";
import { clsx } from "@/utils";
import { formatFlightDayMonth, formatFlightTime, formatTerminal } from "../flightPickup";

const poiTitle = (poi: PointsOfInterest) => poi.rm_label?.trim() || poi.address?.split(",")[0]?.trim() || poi.address;

/**
 * Why a stop names a terminal nobody typed (RM-Client 4002-24040; the drop-off half is DEV-1487). A
 * location that changed itself is the one thing on this card the client cannot account for, so it says
 * which flight moved it and when that flight arrives or leaves — enough to check the claim against
 * their boarding pass.
 *
 * One component for both legs, taking the stop's noun and the flight's verb, because the sentence is
 * the same sentence: only the event differs, and two copies would drift on the wording that makes the
 * badge checkable at all.
 */
const TerminalSwapAlert = ({
  terminal,
  flightNumber,
  iso,
  stop,
  verb,
}: {
  terminal: string;
  flightNumber: string;
  iso: string;
  /** "Pickup" or "Drop-off" — the stop the swap moved. */
  stop: string;
  /** "arriving" or "departing" — what the flight is doing at the instant named. */
  verb: string;
}) => (
  <Alert
    type="warning"
    noIcon
    // Outlined, and tightened past the Alert's own full-width padding — this sits inside a narrow
    // summary card under a 12px address, not across a panel. Same outlined treatment as the Note on
    // the Date & Time panel.
    className="mt-1.5 min-h-0 rounded-md border border-warning px-2.5 py-2"
    message={
      <span className="flex flex-col gap-0.5 text-left">
        {/* The canonical label first: the stored terminal is raw, so a "T3" payload through
            `formatTerminal` alone reads "Terminal T3" while the same terminal as "3" reads "Terminal 3". */}
        <Typography variant="small">{`${stop} updated to ${flightTerminalLabel(terminal) ?? formatTerminal(terminal)}`}</Typography>
        <Typography variant="small-regular">
          {`Based on ${flightNumber} ${verb} ${formatFlightDayMonth(iso)}, ${formatFlightTime(iso)}.`}
        </Typography>
      </span>
    }
  />
);

/**
 * The trip's route as a vertical dotted timeline — pickup (primary dot) → any stops → destination
 * (orange dot). Shared by both Booking Summary variants so the Address box looks identical on the
 * draft steps and the priced Payment step. Type matches the Trip Details rows: 12px semibold label
 * over a 12px regular gray address.
 *
 * `pickupFlightNumber` is a PROP rather than read off the booking slice, because this renders on the
 * confirmation page too — where the draft has been cleared and the flight has to come from the
 * created booking instead. Shown under the FIRST location: the flight belongs to the pickup, and on a
 * trip that merely passes an airport it would be wrong anywhere else.
 *
 * `departureFlightNumber` is its mirror image on the LAST location (DEV-1487): the DEPARTURE FLIGHT
 * belongs to the drop-off. A booking can carry both — an airport-to-airport transfer shows a flight at
 * each end — which is why the two are separate props rather than one "the flight".
 */
export const RouteTimeline = ({
  locations,
  pickupFlightNumber,
  pickupTerminalSource,
  departureFlightNumber,
  departureTerminalSource,
  jobVerified,
}: {
  locations: PointsOfInterest[];
  pickupFlightNumber?: string | null;
  /**
   * The flight that moved the pickup to its arrival terminal, when one did. A prop for the same reason
   * as the flight number, and the reason the confirmation page shows no badge: the swap is a thing that
   * happened while the client was editing, not a fact about the booked trip.
   */
  pickupTerminalSource?: PickupTerminalSource | null;
  /** The DEPARTURE FLIGHT, shown under the drop-off. A prop for the same reason as the pickup's. */
  departureFlightNumber?: string | null;
  /** The flight that moved the DROP-OFF to its departure terminal, when one did. */
  departureTerminalSource?: DepartureTerminalSource | null;
  /** The booking's stored validation flag — a prop for the same reason as the flight number. */
  jobVerified?: boolean;
}) => (
  <div className="flex flex-col">
    {locations.map((poi, index) => {
      const isLast = index === locations.length - 1;
      return (
        <div key={`${poi.uuid || poi.address}-${index}`} className="flex gap-2.5">
          {/* Dot + dashed connector down to the next location */}
          <div className="flex flex-col items-center pt-1">
            <span className={clsx("h-2.5 w-2.5 shrink-0 rounded-full", isLast ? "bg-[#F79009]" : "bg-primary")} />
            {!isLast && <span className="my-1 w-px flex-1 border-l border-dashed border-neutral-mid-gray" />}
          </div>
          <div className={clsx("min-w-0", !isLast && "pb-4")}>
            <Typography variant="small" className="block">
              {poiTitle(poi)}
            </Typography>
            <Typography variant="small-regular" className="block text-neutral-dark-gray">
              {poi.address}
            </Typography>
            {index === 0 && pickupFlightNumber && (
              <Typography variant="small" className="block text-neutral-silver-gray">
                Flight: {pickupFlightNumber}
              </Typography>
            )}
            {index === 0 && pickupTerminalSource && (
              <TerminalSwapAlert
                stop="Pickup"
                verb="arriving"
                terminal={pickupTerminalSource.terminal}
                flightNumber={pickupTerminalSource.flightNumber}
                iso={pickupTerminalSource.arrivalIso}
              />
            )}
            {/* The drop-off's own flight. `isLast` rather than a fixed index, because the timeline
              carries any number of intermediate stops between the two ends. A single-location trip
              cannot happen here — the summary needs a pickup and a drop-off — so the two blocks
              cannot both fire on one row. */}
            {isLast && departureFlightNumber && (
              <Typography variant="small" className="block text-neutral-silver-gray">
                Departure flight: {departureFlightNumber}
              </Typography>
            )}
            {isLast && departureTerminalSource && (
              <TerminalSwapAlert
                stop="Drop-off"
                verb="departing"
                terminal={departureTerminalSource.terminal}
                flightNumber={departureTerminalSource.flightNumber}
                iso={departureTerminalSource.departureIso}
              />
            )}
            {/* Same wording as the bookings table's red flag, so the two surfaces agree. Before this
                the table warned and the sheet said nothing — it just showed a map of the wrong city
                and a $0.00 price, which reads as working. */}
            {isUnvalidatedLocation(poi, jobVerified) && (
              <Typography variant="small" className="flex items-center text-danger">
                Requires Validation
                <Icon name="Danger" size={14} variant="Bold" className="ml-1 inline text-warning" />
              </Typography>
            )}
          </div>
        </div>
      );
    })}
  </div>
);
